package top.wcpe.allincore.dsl.compiler.pipeline

import top.wcpe.allincore.dsl.compiler.ast.AstNode
import top.wcpe.allincore.dsl.compiler.ast.ComponentAst
import top.wcpe.allincore.dsl.compiler.ast.DslMode
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.ast.ThemeFileAst
import top.wcpe.allincore.dsl.compiler.codegen.IrEmitter
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.diag.InMemorySourcesProvider
import top.wcpe.allincore.dsl.compiler.lex.LexException
import top.wcpe.allincore.dsl.compiler.lex.Lexer
import top.wcpe.allincore.dsl.compiler.lex.Token
import top.wcpe.allincore.dsl.compiler.parse.ParseException
import top.wcpe.allincore.dsl.compiler.parse.Parser
import top.wcpe.allincore.dsl.compiler.semantic.SemanticChecker
import top.wcpe.allincore.dsl.compiler.semantic.`native`.ClassRegistry
import top.wcpe.allincore.dsl.compiler.source.ScreenIdResolver
import top.wcpe.allincore.dsl.compiler.source.SourceFile
import top.wcpe.allincore.dsl.compiler.source.SourcePosition

/**
 * 多文件编译流水线：把多个 component 源 + 多个 theme 源 + 一个 screen 源跑通完整管线。
 *
 * 合并要点：
 * - 保留 KMP 侧 [CompilationSourceSet] 路径规范化、[Lexer] 双参（text, path）、
 *   [DiagnosticCollector.stableAll]、[InMemorySourcesProvider]
 * - 并入 master 侧 strict 闭包传播、MaterialGraph / ShaderLayer 校验、
 *   结构化 MultiSourceCompilation 拆分
 */
class ScreenWithComponentsPipeline(
    private val options: CompileOptions = CompileOptions(),
) {

    /** 兼容入口：仅有 component + screen，不带 theme 文件。 */
    fun compile(components: List<SourceFile>, screen: SourceFile): CompileResult =
        compile(components, emptyList(), screen)

    /**
     * 编译入口（带主题预设）。
     *
     * @param components 组件源文件列表
     * @param themes 顶级 theme 源文件列表（每个 `.allin` 内仅一个 `theme { scheme = ... }` 块）
     * @param screen 画面源文件
     */
    fun compile(
        components: List<SourceFile>,
        themes: List<SourceFile>,
        screen: SourceFile,
    ): CompileResult {
        val prepared = when (val result = CompilationSourceSet.prepare(components, themes, screen)) {
            is CompilationSourceSet.Result.Failure -> return CompileResult.Failure(result.diagnostics)
            is CompilationSourceSet.Result.Success -> result.sources
        }
        return MultiSourceCompilation(options, prepared).compile()
    }
}

private class MultiSourceCompilation(
    private val options: CompileOptions,
    private val prepared: CompilationSourceSet.Prepared,
) {
    private val diag = DiagnosticCollector(InMemorySourcesProvider(prepared.sourceTexts))
    private val parser = SourceParser(diag)

    fun compile(): CompileResult {
        // 先解析 screen，供无文件头组件继承所属编译单元的 DSL 模式。
        val screenAst = parser.parseScreen(prepared.entry)
        val strictComponents = screenAst?.let { collectStrictComponents(it) }
        val componentAsts = strictComponents?.let { parseComponents(it) }
        val themeAsts = componentAsts?.let { parseThemes() }
        val registries = themeAsts?.let { prepareRegistries(requireNotNull(componentAsts), it) }
        val expanded = registries?.let { expandScreen(requireNotNull(screenAst), it) }
        val result = expanded?.let { finishCompilation(it, requireNotNull(registries)) }
        return result ?: CompileResult.Failure(diag.stableAll())
    }

    /**
     * strict 只传播到 screen 的 import 闭包；未引用旧组件保持自身 legacy 语义。
     * 返回 null 表示闭包内组件解析失败，应整体失败。
     */
    private fun collectStrictComponents(screenAst: ScreenAst): Map<SourceFile, ComponentAst>? {
        val strictComponents = LinkedHashMap<SourceFile, ComponentAst>()
        if (screenAst.dslHeader?.mode != DslMode.STRICT) {
            return strictComponents
        }
        val sourcesById = prepared.components.associateBy { deriveComponentPath(it.path) }
        val pending = ArrayDeque(screenAst.imports.map { it.path })
        val visited = mutableSetOf<String>()
        while (pending.isNotEmpty()) {
            val componentId = pending.removeFirst()
            // 已访问或不在组件库中的 id 跳过（不存在的路径由后续展开阶段 E300 兜底）
            val source = componentId.takeIf { visited.add(it) }?.let { sourcesById[it] } ?: continue
            when (val ast = parser.parseTopLevel(source, componentId, DslMode.STRICT)) {
                null -> return null
                is ComponentAst -> {
                    strictComponents[source] = ast
                    ast.imports.forEach { pending.addLast(it.path) }
                }
                else -> {
                    reportWrongTopLevel(ast, source, "component")
                    return null
                }
            }
        }
        return strictComponents
    }

    private fun parseComponents(strictComponents: Map<SourceFile, ComponentAst>): List<ComponentAst>? {
        val asts = mutableListOf<ComponentAst>()
        for (source in prepared.components) {
            val preParsed = strictComponents[source]
            if (preParsed != null) {
                asts.add(preParsed)
                continue
            }
            when (val ast = parser.parseTopLevel(source, deriveComponentPath(source.path))) {
                null -> return null
                is ComponentAst -> asts.add(ast)
                else -> {
                    reportWrongTopLevel(ast, source, "component")
                    return null
                }
            }
        }
        return asts
    }

    private fun parseThemes(): List<ThemeFileAst>? {
        val asts = mutableListOf<ThemeFileAst>()
        for (source in prepared.themes) {
            when (val ast = parser.parseTopLevel(source, deriveComponentId(source.path))) {
                null -> return null
                is ThemeFileAst -> asts.add(ast)
                else -> {
                    reportWrongTopLevel(ast, source, "theme")
                    return null
                }
            }
        }
        return asts
    }

    private fun reportWrongTopLevel(ast: AstNode, source: SourceFile, expected: String) {
        diag.error(
            DslError.E003_WRONG_TOPLEVEL_KIND,
            ast.position,
            source.path,
            expected,
            topLevelKindName(ast),
        )
    }

    private fun prepareRegistries(
        componentAsts: List<ComponentAst>,
        themeAsts: List<ThemeFileAst>,
    ): CompilationRegistries? {
        val componentRegistry = ComponentRegistry()
        ComponentCollectorPass(componentRegistry, diag).collect(componentAsts)
        val themeRegistry = ThemeRegistry()
        ThemeCollectorPass(themeRegistry, diag).collect(themeAsts)
        // 主题继承 flatten：把 extends 引用的父主题 token 并入子主题（须在派生之前）。
        ThemeInheritancePass(themeRegistry, diag).flatten(themeAsts)
        // 主题值解析（第一轮）：解析 token 引用别名与颜色函数，使其可作种子 / 引用继承父 token。
        val themePositions = themeAsts.associate { it.schemeName to it.position }
        val themeValueResolver = ThemeValueResolverPass(diag)
        themeValueResolver.resolveRegistry(themeRegistry, themePositions, SourcePosition.UNKNOWN)
        // 种子派生：对含 seed.* 的主题由少量种子补全完整 token。
        DerivativeThemePass(themeRegistry).derive()
        // 主题修饰：对声明 modifiers.compact / highContrast 的主题做几何缩放 / 对比增强。
        ThemeModifierPass(themeRegistry).apply()
        // 主题值解析（第二轮）：解析引用 / 颜色函数指向派生 token 的情形（幂等）。
        themeValueResolver.resolveRegistry(themeRegistry, themePositions, SourcePosition.UNKNOWN)
        return CompilationRegistries(componentRegistry, themeRegistry).takeUnless { hasStrictErrors() }
    }

    private fun expandScreen(screenAst: ScreenAst, registries: CompilationRegistries): ExpandedScreen? {
        // M6-d：把 screen 内联 component 注册进同一 registry，再做展开。
        ComponentCollectorPass(registries.components, diag).collectInline(screenAst.inlineComponents)
        val componentExpander = ComponentExpanderPass(registries.components, diag)
        val componentExpanded = continueWith(screenAst, componentExpander::expand)
        val inventoryExpanded = componentExpanded?.let { continueWith(it, InventoryExpanderPass(diag)::expand) }
        val gridExpanded = inventoryExpanded?.let { continueWith(it, SlotGridExpanderPass(diag)::expand) }
        val themeMerged = gridExpanded?.let { continueWith(it, ThemeMergePass(registries.themes, diag)::expand) }
        themeMerged?.let {
            ComponentTokenSymbolPass(diag).check(it)
        }
        if (hasStrictErrors()) return null
        val modifierExpanded = themeMerged?.let { continueWith(it, ModifierBundleExpandPass(diag)::expand) }
        // master：material graph 与 shaderLayer program 符号校验
        modifierExpanded?.let {
            MaterialGraphValidationPass(diag).check(it)
            ShaderLayerProgramSymbolPass(diag, options.knownShaderPrograms).check(it)
        }
        if (hasStrictErrors()) return null
        // 编译期 style-class 样式层：须在组件展开之后、NativeClassResolver / SemanticChecker 之前
        val styleApplied = modifierExpanded?.let { continueWith(it, StyleClassApplyPass()::apply) }
        val nativeResolved = styleApplied?.let {
            continueWith(it, NativeClassResolverPass(ClassRegistry.withBuiltins(), diag)::expand)
        }
        val awaitChecked = nativeResolved?.let { continueWith(it, AwaitTargetResolverPass(diag)::expand) }
        return awaitChecked?.let {
            ExpandedScreen(
                nativeResolved = it,
                modifierExpanded = requireNotNull(modifierExpanded),
                themeMerged = requireNotNull(themeMerged),
                componentExpander = componentExpander,
                themeRegistry = registries.themes,
            )
        }
    }

    private fun finishCompilation(
        expanded: ExpandedScreen,
        registries: CompilationRegistries,
    ): CompileResult.Success? {
        val knownThemeTokens = collectKnownThemeTokens(expanded.modifierExpanded, registries.themes)
        SemanticChecker(
            diag,
            knownThemeTokens = knownThemeTokens,
            strictThemeTokens = options.strictThemeTokens,
        ).check(expanded.nativeResolved)
        // 分析结束后只要存在 ERROR 就跳过 codegen
        if (diag.hasErrors()) return null
        return emit(expanded)
    }

    private fun emit(expanded: ExpandedScreen): CompileResult.Success {
        val ir = IrEmitter().emit(expanded.nativeResolved, expanded.themeRegistry)
        val usedTheme = (expanded.themeMerged.theme?.tokens?.get("scheme") as? String)?.takeIf { it.isNotBlank() }
        return CompileResult.Success(
            ir,
            diag.stableAll(),
            kind = expanded.nativeResolved.kind,
            usedComponents = expanded.componentExpander.usedComponentIds(),
            usedTheme = usedTheme,
        )
    }

    private fun continueWith(screen: ScreenAst, transform: (ScreenAst) -> ScreenAst): ScreenAst? {
        if (hasStrictErrors()) {
            return null
        }
        val transformed = transform(screen)
        return transformed.takeUnless { hasStrictErrors() }
    }

    private fun hasStrictErrors(): Boolean = options.strict && diag.hasErrors()

    private data class CompilationRegistries(val components: ComponentRegistry, val themes: ThemeRegistry)

    private data class ExpandedScreen(
        val nativeResolved: ScreenAst,
        val modifierExpanded: ScreenAst,
        val themeMerged: ScreenAst,
        val componentExpander: ComponentExpanderPass,
        val themeRegistry: ThemeRegistry,
    )
}

private fun collectKnownThemeTokens(screen: ScreenAst, themeRegistry: ThemeRegistry): Set<String> =
    LinkedHashSet<String>().apply {
        screen.theme?.tokens?.keys?.let { addAll(it) }
        for (scheme in themeRegistry.schemes()) {
            themeRegistry.lookup(scheme)?.keys?.let { addAll(it) }
        }
    }

private class SourceParser(private val diag: DiagnosticCollector) {
    fun parseTopLevel(source: SourceFile, sourceId: String, inheritedDslMode: DslMode? = null): AstNode? =
        parse(source) {
            Parser(it, sourceId, diag, inheritedDslMode).parseTopLevel()
        }

    fun parseScreen(source: SourceFile): ScreenAst? = parse(source) {
        Parser(it, ScreenIdResolver.derive(source.path), diag).parse()
    }

    private fun <T> parse(source: SourceFile, parseTokens: (List<Token>) -> T): T? {
        val tokens = try {
            Lexer(source.text, source.path).lex()
        } catch (exception: LexException) {
            diag.error(exception.error ?: DslError.E001_LEX_ERROR, exception.position, exception.message.orEmpty())
            null
        }
        return tokens?.let {
            try {
                parseTokens(it)
            } catch (exception: ParseException) {
                diag.error(
                    exception.error ?: DslError.E002_PARSE_ERROR,
                    exception.position,
                    exception.message.orEmpty(),
                )
                null
            }
        }
    }
}

/**
 * 组件注册键（ADR-0038）：保留组件库根下的相对路径——
 * `\` 归一为 `/`、去 `.allin` 后缀；
 * 依次剥离 docs 产物前缀 `components-source/` 与库根 `ui-components/`，
 * 使 `components-source/ui-components/PrimaryButton.allin` 与
 * `ui-components/PrimaryButton.allin` 注册为同一键 `PrimaryButton`。
 */
private fun deriveComponentPath(name: String): String {
    val normalized = name.replace('\\', '/').removeSuffix(".allin")
    return normalized
        .removePrefix("components-source/")
        .removePrefix("ui-components/")
}

/** 从资源路径推导主题 scheme id（取文件名去后缀）。 */
private fun deriveComponentId(name: String): String {
    val normalized = name.replace('\\', '/')
    val fileName = normalized.substringAfterLast('/')
    return fileName.removeSuffix(".allin")
}

/** 把顶层 AST 节点翻译成给用户的“类型名”字串，用于 E003 报错。 */
private fun topLevelKindName(ast: AstNode): String = when (ast) {
    is ComponentAst -> "component"
    is ScreenAst -> "screen"
    is ThemeFileAst -> "theme"
    // ::class.simpleName 为 String?（匿名类为 null，顶层 AST 不会命中），兜底空串保持非空返回
    else -> ast::class.simpleName ?: ""
}
